import { Flame } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useRecordContext } from "ra-core";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import type { Contact } from "../../atomic-crm/types";

interface Engagement {
  score: number;
  label: "hot" | "warm" | "cold";
  recent_replies: number;
  recent_clicks: number;
  accepted_estimate: boolean;
  upcoming_appointments: number;
}

const TONES: Record<Engagement["label"], string> = {
  hot: "bg-red-500/10 text-red-700",
  warm: "bg-amber-500/10 text-amber-700",
  cold: "bg-sky-500/10 text-sky-700",
};

/**
 * The engagement score, with its receipts.
 *
 * The tooltip spells out WHY ("2 replies · accepted an estimate") because a
 * score nobody can explain is a score nobody trusts, the rubric is additive
 * and transparent by design (see the scoring migration).
 */
export const EngagementBadge = () => {
  const record = useRecordContext<Contact>();

  const { data } = useQuery<Engagement | null>({
    queryKey: ["engagement", record?.id],
    enabled: Boolean(record?.id),
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("contact_engagement")
        .select(
          "score,label,recent_replies,recent_clicks,accepted_estimate,upcoming_appointments",
        )
        .eq("contact_id", record!.id)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data as Engagement | null;
    },
  });

  if (!data) return null;

  const reasons = [
    data.recent_replies > 0 &&
      `${data.recent_replies} repl${data.recent_replies === 1 ? "y" : "ies"}`,
    data.recent_clicks > 0 &&
      `${data.recent_clicks} link click${data.recent_clicks === 1 ? "" : "s"}`,
    data.accepted_estimate && "accepted an estimate",
    data.upcoming_appointments > 0 && "upcoming appointment",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <span
      className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-medium ${TONES[data.label]}`}
      title={reasons || "No engagement signals in the last 90 days"}
    >
      <Flame className="size-3" />
      {data.label} · {data.score}
    </span>
  );
};
